import React, { useMemo, useState } from 'react';
import { CreateTextPlaceholderModal } from './CreateTextPlaceholderModal';
import { useTextPlaceholdersRequired } from './TextPlaceholderContext';
import styles from './scenario-studio.module.css';

export function TextPlaceholdersEditor() {
  const { entries, loading, upsertPlaceholder } = useTextPlaceholdersRequired();
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => a.key.localeCompare(b.key)),
    [entries]
  );

  const handleSaveValue = async (key: string) => {
    const value = (draftValues[key] ?? entries.find((entry) => entry.key === key)?.value ?? '').trim();
    if (!value) {
      setError(`La valeur pour @${key} ne peut pas être vide.`);
      return;
    }
    setSavingKey(key);
    setError(null);
    try {
      await upsertPlaceholder(key, value);
      setDraftValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de l\'enregistrement');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className={styles.phoneSection}>
      <div className={styles.row} style={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <h4>Placeholders texte</h4>
        <button type="button" className={styles.btnSecondary} onClick={() => setCreateOpen(true)}>
          + Nouvelle clé
        </button>
      </div>
      <p className={styles.hint}>
        Clés partagées entre scénarios, valeurs propres à ce scénario. Dans l&apos;éditeur inline, tapez{' '}
        <code>@cle</code> pour insérer une référence.
      </p>
      {error ? <p className={styles.errorText}>{error}</p> : null}
      {loading ? <p className={styles.hint}>Chargement…</p> : null}
      {!loading && sortedEntries.length === 0 ? (
        <p className={styles.hint}>Aucune clé globale pour l&apos;instant.</p>
      ) : null}
      {sortedEntries.map((entry) => {
        const currentValue = draftValues[entry.key] ?? entry.value ?? '';
        return (
          <div key={entry.key} className={styles.row} style={{ alignItems: 'flex-end', gap: '0.75rem' }}>
            <div className={styles.field} style={{ flex: '0 0 10rem' }}>
              <label>
                <code>@{entry.key}</code>
              </label>
              {entry.label ? <p className={styles.hint}>{entry.label}</p> : null}
            </div>
            <div className={styles.field} style={{ flex: 1 }}>
              <label>Valeur (ce scénario)</label>
              <input
                className={styles.input}
                value={currentValue}
                placeholder="non définie"
                onChange={(event) =>
                  setDraftValues((prev) => ({ ...prev, [entry.key]: event.target.value }))
                }
              />
            </div>
            <button
              type="button"
              className={styles.btnSecondary}
              disabled={savingKey === entry.key}
              onClick={() => handleSaveValue(entry.key)}
            >
              {savingKey === entry.key ? '…' : 'Enregistrer'}
            </button>
          </div>
        );
      })}
      {createOpen ? (
        <CreateTextPlaceholderModal
          initialKey=""
          onClose={() => setCreateOpen(false)}
          onSubmit={async (payload) => {
            await upsertPlaceholder(payload.key, payload.value, payload.label);
            setCreateOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
